import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { CdkDragSortEvent, moveItemInArray } from '@angular/cdk/drag-drop';
import { DataRefsByKey, IngredientType, MarketingFood, Unit } from '@shared/models/dataref.model';
import { RefDataService } from '@services/ref-data.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-ingredients-block',
  templateUrl: './ingredients-block.component.html',
  styleUrls: ['./ingredients-block.component.scss'],
  standalone: false,
})
export class IngredientsBlockComponent implements OnChanges, AfterViewChecked, OnDestroy, OnInit {
  @Input() formArray?: UntypedFormArray;
  @Input() units?: DataRefsByKey<Unit> | null;
  @Input() domain = '';
  @Input() market = '';
  @Input() lang = '';
  @Input() marketingFoods: MarketingFood[] = [];
  @Input() type = '';
  @Input() usedIngredientTypes = new Set<string>();
  @Input() ingredientTypes: IngredientType[] = [];
  @Input() index: number;
  @Output() addLine = new EventEmitter<{ index: number; applicationDescription: string; type: string } | { type: string }>();
  @Output() deleteBlock = new EventEmitter<string>();
  @Output() selectIngredientType = new EventEmitter<{ oldType: string; newType: string }>();

  @ViewChildren('ingredientLineNameField') ingredientLineNameFields?: QueryList<ElementRef<HTMLInputElement>>;

  formGroups?: UntypedFormGroup[];
  unitsArray?: Unit[];
  lineAdded = false;
  typesAvailable: IngredientType[] = [];

  destroy$ = new Subject<void>();

  typeControl = new UntypedFormControl(null, this.validatorType());

  constructor(private readonly refDataService: RefDataService) {}

  ngOnInit(): void {
    this.typeControl.setValue(this.type);
    this.typeControl.markAsDirty();
    this.typeControl.updateValueAndValidity();
    this.typeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => this.onTypeSelection(val));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.setTypes) {
      this.typesAvailable = this.availableTypes();
    }

    if (changes.formArray && this.formArray) {
      this.formGroups = this.formArray.controls as UntypedFormGroup[];
    }

    if (changes.units) {
      this.unitsArray = this.refDataService.alphabeticOrder(
        this.refDataService.toArray<Unit>(changes.units.currentValue).filter((unit: Unit) => !!unit.name[this.market]),
        'labelInt',
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    this.typesAvailable = this.availableTypes();
    if (this.lineAdded) {
      this.ingredientLineNameFields?.last?.nativeElement.focus();
      this.lineAdded = false;
    }
  }

  reorder(event: CdkDragSortEvent<UntypedFormGroup[]>): void {
    if (event.previousIndex !== event.currentIndex && this.formArray) {
      if (this.formArray.controls[event.previousIndex].value.type !== this.type) {
        this.formArray.controls[event.previousIndex].patchValue({ type: this.type });
      }
      moveItemInArray(this.formArray.controls, event.previousIndex, event.currentIndex);
      this.formGroups = this.formArray.controls as UntypedFormGroup[];
      this.formArray.markAsDirty();
      this.formArray.updateValueAndValidity();
    }
  }

  delete(index: number): void {
    if (this.formArray) {
      this.formArray.removeAt(index);
      this.formArray.markAsDirty();
      this.formGroups = this.formArray.controls as UntypedFormGroup[];
    }
  }

  getFoodFormControl(formGroupIngredient: UntypedFormGroup): UntypedFormControl {
    return formGroupIngredient.get('food') as UntypedFormControl;
  }

  getRequestedFoodFormControl(formGroupIngredient: UntypedFormGroup): UntypedFormControl {
    return formGroupIngredient.get('requestedFood') as UntypedFormControl;
  }

  getSuggestedMarketingFoods(formGroupIngredient: UntypedFormGroup): string[] {
    return formGroupIngredient?.get('suggestedMarketingFoods')?.value ?? [];
  }

  focusNextLine(index: number): void {
    if (index === (this.ingredientLineNameFields?.length ?? 0) - 1) {
      this.newLine();
      return;
    }
    this.ingredientLineNameFields?.get(index + 1)?.nativeElement?.focus();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  fastInput(event: any, index: number): void {
    const pastedData = event.clipboardData.getData('text/plain');
    const newIngredients = pastedData?.trim().split('\n');
    const type = this.type;
    if (newIngredients.length > 1) {
      // Prevent paste on the clicked line
      event.preventDefault();
      event.stopPropagation();
      newIngredients.map((ingredientDescription: string, indexIngr: number) => {
        const form = this.formArray?.controls[index] as UntypedFormGroup;
        if (
          indexIngr === 0 ||
          (form !== undefined && (form.controls.applicationDescription.value === null || form.controls.applicationDescription.value === ''))
        ) {
          form.controls.applicationDescription.patchValue(ingredientDescription);
        } else {
          // add
          this.addLine.emit({ index, applicationDescription: ingredientDescription, type });
        }
        index++;
      });
      this.lineAdded = true;
    }
  }

  newLine(): void {
    const type = this.type;
    this.lineAdded = true;
    this.addLine.emit({ type });
  }

  removeBlock(): void {
    this.deleteBlock.emit(this.type);
  }

  availableTypes(): IngredientType[] {
    return this.ingredientTypes
      .filter((ingredientType) => !this.usedIngredientTypes.has(ingredientType.key) || ingredientType.key === this.type)
      .sort((a, b) => (a.translatedName?.toString() ?? '').localeCompare(b.translatedName?.toString() ?? ''));
  }

  onTypeSelection(selectedType: string): void {
    this.formGroups?.forEach((ingredient) => {
      if (ingredient.value.type === this.type) {
        ingredient.patchValue({ type: selectedType });
        ingredient.get('type')?.markAsDirty();
        this.selectIngredientType.emit({ oldType: this.type, newType: selectedType });
      }
    });
    this.type = selectedType;
    this.typesAvailable = this.availableTypes();
  }

  validatorType(): ValidatorFn {
    return () => {
      if (!this.type || this.type === 'default') {
        return { type: false };
      }

      return this.formArray?.controls
        .filter((control) => control.value.type === this.type)
        .reduce((prev: boolean, cur: AbstractControl) => prev && (cur.valid || cur.pending), true)
        ? null
        : { type: false };
    };
  }
}
